import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { requestsAPI, adminAPI } from '../../../services/api';
import { formatDate, formatStatus } from '../../../utils/formatters';
import { toast } from 'react-toastify';

// Components
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';
import SuccessAlert from '../../../components/SuccessAlert';
import StatusBadge from '../../../components/StatusBadge';
import RequestCostsManager from '../../../components/RequestCostsManager';
import CrossDockBillingManager from '../../../components/CrossDockBillingManager';
import DocumentsTab from '../../../components/DocumentsTab';
import PhotosTab from '../../../components/PhotosTab';
import ActivityTimeline from '../../../components/ActivityTimeline';


const CrossDockRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState(false);
  const [invoiceCreated, setInvoiceCreated] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [expandedPalletGroups, setExpandedPalletGroups] = useState(new Set());

  // Workflow modals
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receivePalletId, setReceivePalletId] = useState(null); // null = receive all
  const [receiveNotes, setReceiveNotes] = useState('');
  const [showBookShipmentModal, setShowBookShipmentModal] = useState(false);
  const [shipmentForm, setShipmentForm] = useState({
    carrier: '', serviceLevel: '', trackingNumber: '', bolNumber: '', cost: '', shipDate: ''
  });
  const [selectedPalletIds, setSelectedPalletIds] = useState([]);

  
  // Clear any cached data for this request when component mounts
  useEffect(() => {
    queryClient.removeQueries(['request', id]);
    queryClient.invalidateQueries(['request', id]);
  }, [id, queryClient]);
  
  // Fetch request details
  const { 
    data: request, 
    isLoading, 
    error,
    refetch 
  } = useQuery(
    ['request', id], 
    () => adminAPI.getRequest(id),
    {
      retry: 2,
      enabled: !!id,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      staleTime: 0,
    }
  );

  // Handle status update
  const handleStatusUpdate = (newStatus) => {
    setStatusUpdateSuccess(true);
    refetch();
    setTimeout(() => setStatusUpdateSuccess(false), 3000);
  };
  
  // Handle invoice created
  const handleInvoiceCreated = (invoice) => {
    setInvoiceCreated(true);
    refetch();
    setTimeout(() => setInvoiceCreated(false), 3000);
  };

  // Workflow actions
  const handleReceive = async () => {
    try {
      if (receivePalletId) {
        // Single pallet from the table action
        await adminAPI.receiveCrossDockPallet(receivePalletId, { notes: receiveNotes });
        toast.success('Pallet received');
      } else if (selectedPalletIds.length > 0) {
        // Selected pallets from the modal
        for (const palletId of selectedPalletIds) {
          await adminAPI.receiveCrossDockPallet(palletId, { notes: receiveNotes });
        }
        toast.success(`${selectedPalletIds.length} pallet(s) received`);
      } else {
        // Receive all
        await adminAPI.receiveAllCrossDockPallets(id, { notes: receiveNotes });
        toast.success('All pallets received');
      }
      setShowReceiveModal(false);
      setReceiveNotes('');
      setReceivePalletId(null);
      setSelectedPalletIds([]);
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to receive');
    }
  };

  const handleBookShipment = async () => {
    try {
      const ids = selectedPalletIds.length > 0 ? selectedPalletIds
        : request.CrossDockPallets?.filter(p => ['received', 'awaiting_customer_docs'].includes(p.status)).map(p => p.id) || [];
      if (ids.length === 0) { toast.error('No eligible pallets'); return; }
      await adminAPI.bookCrossDockShipment({ palletIds: ids, ...shipmentForm });
      toast.success('Shipment booked');
      setShowBookShipmentModal(false);
      setShipmentForm({ carrier: '', serviceLevel: '', trackingNumber: '', bolNumber: '', cost: '', shipDate: '' });
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to book shipment');
    }
  };

  const handleMarkShipped = async (palletIds) => {
    try {
      await adminAPI.markCrossDockShipped({ palletIds });
      toast.success('Pallets marked as shipped');
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to mark shipped');
    }
  };

  // Toggle pallet group expansion
  const togglePalletGroup = (groupKey) => {
    const newExpanded = new Set(expandedPalletGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedPalletGroups(newExpanded);
  };

  // Group pallets by identical specifications
  const groupPalletsBySpecs = (pallets) => {
    if (!pallets || pallets.length === 0) return [];
    
    const groups = {};
    pallets.forEach(pallet => {
      const key = `${pallet.commodity}-${pallet.length}x${pallet.width}x${pallet.height}-${pallet.gross_weight}`;
      if (!groups[key]) {
        groups[key] = {
          commodity: pallet.commodity,
          dimensions: { length: pallet.length, width: pallet.width, height: pallet.height },
          weight: pallet.gross_weight,
          pallets: [],
          totalQuantity: 0
        };
      }
      groups[key].pallets.push(pallet);
      groups[key].totalQuantity += pallet.pallet_quantity || 1;
    });
    
    return Object.entries(groups).map(([key, group]) => ({ key, ...group }));
  };

  // Format cross-dock metadata
  const parseCrossDockMetadata = (specialInstructions) => {
    try {
      return JSON.parse(specialInstructions);
    } catch {
      return {};
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message={error.message || 'Error loading request'} />;
  if (!request) return <ErrorAlert message="Request not found or access denied" />;
  if (request.requestType !== 'crossdock') {
    return <ErrorAlert message="This page is only for cross-dock requests" />;
  }

  const crossDockData = parseCrossDockMetadata(request.specialInstructions);
  const palletGroups = groupPalletsBySpecs(request.CrossDockPallets);
  const totalPallets = request.CrossDockPallets?.reduce((sum, p) => sum + (p.pallet_quantity || 1), 0) || 0;
  const isCustomerArranges = crossDockData.shippingArrangement === 'customer';
  const pendingPallets = request.CrossDockPallets?.filter(p => p.status === 'pending') || [];
  const hasPendingPallets = pendingPallets.length > 0;
  const hasReceivedPallets = request.CrossDockPallets?.some(p => ['received', 'awaiting_customer_docs'].includes(p.status));
  const hasReadyPallets = request.CrossDockPallets?.some(p => ['ready_for_pickup', 'ready_to_ship'].includes(p.status));
  const readyPalletIds = request.CrossDockPallets?.filter(p => ['ready_for_pickup', 'ready_to_ship'].includes(p.status)).map(p => p.id) || [];

  return (
    <div className="py-6">
      {statusUpdateSuccess && (
        <SuccessAlert message="Request status updated successfully" />
      )}
      
      {invoiceCreated && (
        <SuccessAlert message="Invoice created successfully" />
      )}
      
      {/* Breadcrumbs */}
      <nav className="text-sm text-gray-500 mb-2">
        <Link to="/admin/requests" className="hover:text-gray-700">Requests</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900">{request?.requestNumber || 'Cross-Dock'}</span>
      </nav>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          Cross-Dock Request {request?.InternalWarehouse ? `to ${request.InternalWarehouse.name}` : ''}
        </h1>
        <div className="flex items-center space-x-3">
          {hasPendingPallets && (
            <button onClick={() => { setReceivePalletId(null); setShowReceiveModal(true); }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700">
              Receive
            </button>
          )}
          {hasReceivedPallets && !isCustomerArranges && (
            <button onClick={() => setShowBookShipmentModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
              Book Shipment
            </button>
          )}
          {hasReadyPallets && (
            <button onClick={() => handleMarkShipped(readyPalletIds)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
              Mark Shipped
            </button>
          )}
        </div>
      </div>
      
      {/* Tabs - Include Shipping tab for cross-dock */}
      <div className="mb-6">
        <div className="sm:hidden">
          <select
            id="tabs"
            name="tabs"
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
          >
            <option value="details">Details</option>
            <option value="shipping">Shipping</option>
            <option value="documents">Documents</option>
            <option value="photos">Photos</option>
            <option value="billing">Billing</option>
            <option value="timeline">Timeline</option>
          </select>
        </div>
        <div className="hidden sm:block">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('details')}
                className={`${
                  activeTab === 'details'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Request Details
              </button>
              <button
                onClick={() => setActiveTab('shipping')}
                className={`${
                  activeTab === 'shipping'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Shipping
              </button>
              <button
                onClick={() => setActiveTab('documents')}
                className={`${
                  activeTab === 'documents'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Documents
              </button>
              <button
                onClick={() => setActiveTab('photos')}
                className={`${
                  activeTab === 'photos'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Photos
              </button>
              <button
                onClick={() => setActiveTab('billing')}
                className={`${
                  activeTab === 'billing'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Billing
              </button>
              <button
                onClick={() => setActiveTab('timeline')}
                className={`${
                  activeTab === 'timeline'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Timeline
              </button>
            </nav>
          </div>
        </div>
      </div>
      
      {/* Details Tab */}
      {activeTab === 'details' && (
        <>
          {/* Request Information -- 2 column layout */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Cross-Dock Request
                </h3>
              </div>
              <StatusBadge status={request.status} />
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Customer</dt>
                  <dd className="mt-1 text-sm text-gray-900">{request?.Company?.name || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Request Type</dt>
                  <dd className="mt-1 text-sm text-gray-900">Cross-Dock</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Request Number</dt>
                  <dd className="mt-1 text-sm text-gray-900">{request?.requestNumber || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Request Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">{request?.createdAt ? formatDate(request.createdAt) : '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Arrival Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">{crossDockData.expectedArrivalDate ? formatDate(crossDockData.expectedArrivalDate) : '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Pickup Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">{crossDockData.expectedPickupDate ? formatDate(crossDockData.expectedPickupDate) : '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Total Pallets</dt>
                  <dd className="mt-1 text-sm text-gray-900">{totalPallets}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Shipping Arrangement</dt>
                  <dd className="mt-1 text-sm text-gray-900">{isCustomerArranges ? 'Customer Arranges' : 'DataVisual Arranges'}</dd>
                </div>

                {(() => {
                  // Source: use ExternalWarehouse association, fall back to JSON
                  const src = request?.ExternalWarehouse || crossDockData.sourceAddress;
                  if (!src) return null;
                  return (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Source Address</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {src.name && <div className="font-medium">{src.name}</div>}
                        <div>{src.street_address || ''}</div>
                        {src.address_line_2 && <div>{src.address_line_2}</div>}
                        <div>{[src.city, src.state_province, src.postal_code].filter(Boolean).join(', ')}</div>
                        {src.country && <div>{src.country}</div>}
                      </dd>
                    </div>
                  );
                })()}

                {(() => {
                  // Destination: use the first pallet's final destination or JSON data
                  const firstPallet = request?.CrossDockPallets?.[0];
                  const dest = crossDockData.selectedDestinationDetails || crossDockData.crossDockDestination;
                  if (!dest || typeof dest !== 'object') return null;
                  return (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Final Destination</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {dest.name && <div className="font-medium">{dest.name}</div>}
                        <div>{dest.street_address || ''}</div>
                        {dest.address_line_2 && <div>{dest.address_line_2}</div>}
                        <div>{[dest.city, dest.state_province, dest.postal_code].filter(Boolean).join(', ')}</div>
                        {dest.country && <div>{dest.country}</div>}
                      </dd>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
          
          {/* Cross-Dock Pallets */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Cross-Dock Pallets
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Pallet specifications grouped by commodity and dimensions.
                </p>
              </div>

            </div>
            <div className="border-t border-gray-200">
              {palletGroups.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {palletGroups.map((group) => (
                    <div key={group.key} className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <button
                              onClick={() => togglePalletGroup(group.key)}
                              className="flex items-center space-x-2 text-left"
                            >
                              <svg
                                className={`h-5 w-5 text-gray-400 transition-transform ${
                                  expandedPalletGroups.has(group.key) ? 'rotate-90' : ''
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                              <div>
                                <h4 className="text-lg font-medium text-gray-900">{group.commodity}</h4>
                                <p className="text-sm text-gray-500">
                                  {group.totalQuantity} pallets • {group.dimensions.length}" × {group.dimensions.width}" × {group.dimensions.height}" • {group.weight} lbs
                                </p>
                              </div>
                            </button>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                            {group.pallets.length} unique pallets
                          </span>
                        </div>
                      </div>

                      {expandedPalletGroups.has(group.key) && (
                        <div className="mt-4">
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Pallet Label
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Quantity
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Notes
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {group.pallets.map((pallet) => (
                                  <tr key={pallet.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {pallet.pallet_label}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {pallet.pallet_quantity || 1}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        pallet.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                        pallet.status === 'received' ? 'bg-green-100 text-green-800' :
                                        pallet.status === 'awaiting_customer_docs' ? 'bg-orange-100 text-orange-800' :
                                        pallet.status === 'ready_for_pickup' || pallet.status === 'ready_to_ship' ? 'bg-indigo-100 text-indigo-800' :
                                        pallet.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                                        pallet.status === 'delivered' ? 'bg-gray-100 text-gray-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {pallet.status.replace(/_/g, ' ')}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                      {pallet.notes || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                      <div className="flex items-center space-x-2">
                                        {pallet.status === 'pending' && (
                                          <button
                                            onClick={() => { setReceivePalletId(pallet.id); setShowReceiveModal(true); }}
                                            className="inline-flex items-center px-2.5 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                                          >
                                            Receive
                                          </button>
                                        )}
                                        {['ready_for_pickup', 'ready_to_ship'].includes(pallet.status) && (
                                          <button
                                            onClick={() => handleMarkShipped([pallet.id])}
                                            className="inline-flex items-center px-2.5 py-1 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
                                          >
                                            Mark Shipped
                                          </button>
                                        )}
                                        <button
                                          onClick={() => {
                                            const pw = window.open('', '_blank');
                                            pw.document.write(`<html><head><title>${pallet.pallet_label}</title></head><body style="font-family:sans-serif;padding:20px;"><div style="border:2px solid #000;padding:20px;text-align:center;"><h2>${pallet.pallet_label}</h2><p><strong>Commodity:</strong> ${group.commodity}</p><p><strong>Dimensions:</strong> ${group.dimensions.length}" x ${group.dimensions.width}" x ${group.dimensions.height}"</p><p><strong>Weight:</strong> ${group.weight} lbs</p></div></body></html>`);
                                            pw.document.close();
                                            pw.print();
                                          }}
                                          className="inline-flex items-center px-2.5 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                                        >
                                          Print Label
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center text-gray-500">
                  No pallets configured for this cross-dock request.
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Shipping Tab */}
      {activeTab === 'shipping' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Shipping Information
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Cross-dock shipping details and tracking information.
            </p>
          </div>
          <div className="border-t border-gray-200">
            <dl>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Shipping Arrangement</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {crossDockData.shippingArrangement === 'customer' ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Customer Arranges Shipping
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      DataVisual Arranges Shipping
                    </span>
                  )}
                </dd>
              </div>

              {crossDockData.deliveryRequirements && (
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Delivery Requirements</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {Array.isArray(crossDockData.deliveryRequirements)
                      ? crossDockData.deliveryRequirements.join(', ')
                      : typeof crossDockData.deliveryRequirements === 'object'
                        ? Object.entries(crossDockData.deliveryRequirements)
                            .filter(([, value]) => value)
                            .map(([key]) => key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()))
                            .join(', ') || 'None'
                        : crossDockData.deliveryRequirements}
                  </dd>
                </div>
              )}

              {crossDockData.siteContactName && (
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Site Contact</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <div>{crossDockData.siteContactName}</div>
                    {crossDockData.siteContactPhone && <div>{crossDockData.siteContactPhone}</div>}
                  </dd>
                </div>
              )}

              {request.trackingNumber && (
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Tracking Number</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {request.trackingNumber}
                  </dd>
                </div>
              )}

              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Special Instructions</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {crossDockData.specialInstructions || '-'}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}
      
      {/* Documents Tab */}
      {activeTab === 'documents' && request && (
        <DocumentsTab requestId={request.id} />
      )}

      {/* Photos Tab */}
      {activeTab === 'photos' && request && (
        <PhotosTab requestId={request.id} />
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Billing Summary for {request?.requestNumber || 'Request'}
            </h3>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <CrossDockBillingManager
              request={request}
              onInvoiceCreated={handleInvoiceCreated}
            />
          </div>
        </div>
      )}


      {/* Timeline Tab */}
      {activeTab === 'timeline' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <ActivityTimeline
              fetchFn={(params) => adminAPI.getEntityTimeline('Request', id, params)}
              entityType="Request"
              entityId={id}
              limit={25}
              title={null}
            />
          </div>
        </div>
      )}

      {/* Receive Modal */}
      {showReceiveModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowReceiveModal(false)}></div>
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {receivePalletId ? 'Receive Pallet' : 'Receive Pallets'}
              </h3>

              {!receivePalletId && (
                <div className="mb-4">
                  <div className="flex space-x-3 mb-4">
                    <button
                      onClick={() => setSelectedPalletIds([])}
                      className={`px-3 py-1.5 text-sm rounded-md ${selectedPalletIds.length === 0 ? 'bg-green-100 text-green-800 font-medium' : 'bg-gray-100 text-gray-600'}`}
                    >
                      Receive All ({pendingPallets.length})
                    </button>
                    <button
                      onClick={() => setSelectedPalletIds(pendingPallets.length > 0 ? [pendingPallets[0].id] : [])}
                      className={`px-3 py-1.5 text-sm rounded-md ${selectedPalletIds.length > 0 ? 'bg-green-100 text-green-800 font-medium' : 'bg-gray-100 text-gray-600'}`}
                    >
                      Select Pallets
                    </button>
                  </div>

                  {selectedPalletIds.length > 0 && (
                    <div className="border rounded-md divide-y max-h-48 overflow-y-auto">
                      {pendingPallets.map(p => (
                        <label key={p.id} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedPalletIds.includes(p.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedPalletIds(prev => [...prev, p.id]);
                              } else {
                                setSelectedPalletIds(prev => prev.filter(id => id !== p.id));
                              }
                            }}
                            className="h-4 w-4 text-green-600 border-gray-300 rounded mr-3"
                          />
                          <span className="text-sm text-gray-900">{p.pallet_label}</span>
                          <span className="text-xs text-gray-500 ml-2">({p.pallet_quantity || 1} pallet)</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes (optional)</label>
                  <textarea
                    value={receiveNotes}
                    onChange={(e) => setReceiveNotes(e.target.value)}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Condition notes, observations..."
                  />
                </div>
                <p className="text-sm text-gray-500">
                  Photos can be uploaded from the Photos tab after receiving.
                </p>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button onClick={() => { setShowReceiveModal(false); setSelectedPalletIds([]); }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  Cancel
                </button>
                <button onClick={handleReceive}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700">
                  {receivePalletId ? 'Receive Pallet' : selectedPalletIds.length > 0 ? `Receive ${selectedPalletIds.length} Pallet(s)` : 'Receive All'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Book Shipment Modal */}
      {showBookShipmentModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowBookShipmentModal(false)}></div>
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Book Outbound Shipment</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Carrier</label>
                    <input type="text" value={shipmentForm.carrier}
                      onChange={(e) => setShipmentForm(f => ({ ...f, carrier: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                      placeholder="FedEx, UPS, etc." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Service Level</label>
                    <input type="text" value={shipmentForm.serviceLevel}
                      onChange={(e) => setShipmentForm(f => ({ ...f, serviceLevel: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm"
                      placeholder="Ground, Express, etc." />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">BOL Number</label>
                    <input type="text" value={shipmentForm.bolNumber}
                      onChange={(e) => setShipmentForm(f => ({ ...f, bolNumber: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tracking Number (optional)</label>
                    <input type="text" value={shipmentForm.trackingNumber}
                      onChange={(e) => setShipmentForm(f => ({ ...f, trackingNumber: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Ship Date</label>
                    <input type="date" value={shipmentForm.shipDate}
                      onChange={(e) => setShipmentForm(f => ({ ...f, shipDate: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Shipping Cost ($)</label>
                    <input type="number" step="0.01" min="0" value={shipmentForm.cost}
                      onChange={(e) => setShipmentForm(f => ({ ...f, cost: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm" />
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  Documents (BOL, etc.) can be uploaded from the Documents tab.
                </p>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button onClick={() => setShowBookShipmentModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  Cancel
                </button>
                <button onClick={handleBookShipment}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700">
                  Book Shipment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrossDockRequestDetail;
